import { Vector2 } from "./vector2";
import { ProductKind } from "../data/products";
import { Signal, computed, signal } from "@lit-labs/preact-signals";
import { mapData } from "../controllers/mapData";

export class Product {
  readonly index: number;
  readonly kind: ProductKind;
  // Cost is how much the product costs to restock
  readonly cost: number;
  readonly maxPrice: number;
  readonly position: Vector2;
  readonly size: Vector2;
  readonly stockLimit: number;
  readonly stock: Signal<number>;
  readonly heldStock: Signal<number>;
  readonly availableStock: Signal<number>;
  // Price is how much the product is being sold for
  public price: number;
  public demand: number;

  constructor(
    index: number,
    kind: ProductKind,
    cost: number,
    maxPrice: number,
    stockLimit: number,
    opts?: {
      price: number;
      demand: number;
      stock: number;
      heldStock: number;
    },
  ) {
    this.index = index;
    this.kind = kind;
    this.cost = cost;
    this.price = opts?.price ?? cost + 1;
    this.maxPrice = maxPrice;
    this.stockLimit = stockLimit;
    this.stock =
      opts?.stock != undefined ? signal(opts.stock) : signal(stockLimit);
    this.heldStock =
      opts?.heldStock != undefined ? signal(opts.heldStock) : signal(0);
    this.availableStock = computed(
      () => this.stock.value + this.heldStock.value,
    );
    this.demand = opts?.demand ?? 0;

    // predetermined values based on the product's index
    const p = mapData.products[index];
    this.position = new Vector2(p.x, p.y);
    this.size = new Vector2(p.w, p.h);
  }

  toJSON = () => ({
    index: this.index,
    kind: this.kind,
    cost: this.cost,
    price: this.price,
    maxPrice: this.maxPrice,
    stock: this.stock.value,
    heldStock: this.heldStock.value,
    stockLimit: this.stockLimit,
    demand: this.demand,
  });

  /** This function returns a normalized [0, 1] value of
   * the likelihood that a customer will not pay for the product.
   * This is based on the distance of the current price from the stock cost.
   */
  getChanceToFail = (): number => {
    // Clamp to 0 and round to 2 decimal places
    const safePercentage = this.kind.index === 0 ? 0.5 : 0.33;
    return Math.max(
      +(
        (this.price - this.cost) / (this.maxPrice - this.cost) -
        safePercentage
      ).toFixed(2),
      0,
    );
  };

  public getMarketingCost(initialCost: number, costPerLevel: number): number {
    return initialCost + Math.floor(costPerLevel * this.demand);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromJSON = (obj: any): Product =>
    new Product(
      obj.index,
      new ProductKind(obj.kind.group, obj.kind.index),
      obj.cost,
      obj.maxPrice,
      obj.stockLimit,
      {
        price: obj.price,
        demand: obj.demand,
        stock: obj.stock,
        heldStock: obj.heldStock,
      },
    );
}
